import React from "react";
import { MicroserviceEventsContext } from "./MicroserviceEventsContext";
import { MicroserviceEventProviderProps } from "../types";

export const MicroserviceEventsProvider = (
    { children, query } : Readonly<MicroserviceEventProviderProps>
) => {
    const [sort, setSort] = React.useState("CreatedDate+desc");
    const [pageLimit, setPageLimit] = React.useState(50);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [selectedReport, _setReport] = React.useState(null);
    const [isClearing, setIsClearing] = React.useState(false);
    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState(false);
    const [load, { data, isLoading: isLoadingData, isFetching, ...loadResults }] = query();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isLoading = isLoadingData === true;
    const isNextPageLoading = isFetching === true || isLoadingData === true || isLoadingState === true;

    const clear = () => {
        if (isNextPageLoading) {
            return;
        }

        setIsLoadingState(_ => true);
        setIsClearing(_ => true);

        const newCacheKey = new Date().valueOf();
        load({ 
            page,
            limit: pageLimit,
            sort,
            searchTerm,
            cacheKey: newCacheKey
        }).unwrap().then(_ => {
            setCacheKey(_prev => newCacheKey);
        }).finally(_ => {
            setIsLoadingState(_prev => false);
            setIsClearing(_ => false);
        });
    };

    const isRowLoaded = (index: number) => {
        return !hasMore || index < results.length;
    };

    const loadMore = () => {
        if (isNextPageLoading || !hasMore) {
            return;
        }

        setIsLoadingState(_ => true);
        load({
            page: page + 1,
            limit: pageLimit,
            sort,
            searchTerm,
            cacheKey
        }).unwrap().then(_ => {
            setIsLoadingState(_prev => false);
        });
    };

    const sortTable = (property: string) => {
        // we want the sort to initially be descending ??
        let newDirection : string | null = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
        clear();
    };

    React.useEffect(clear, [ searchTerm, sort ]);

    return (
        <MicroserviceEventsContext.Provider value={{
            ...loadResults,
            clear,
            data: isClearing ? [] : results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => {} : loadMore, 
            newRecordsSinceCache,
            page,
            pagination,
            searchTerm,
            selectedReport,
            setSearchTerm,
            sort,
            sortTable,
            totalCount,
            totalPages
        }}>
            {children}
        </MicroserviceEventsContext.Provider>
    );
};